// apps/client/src/title/showcase.ts (title screen, 2026-10-05) — the town behind the title, built locally from tuning.title.seed with
// the shared generator (no server call): the island, its civic buildings and trees, friends' houses in their colours and villagers'
// themed houses on the plots nearest the plaza, and the villagers who live in them. Pure data, so it can be made off the main thread
// (title/showcaseWorker.ts). Nobody here has a name on screen: the friends are only colours, as the town says nothing about who.
import { tuning, villagerBank, type FriendColor, type HouseLevel, type RoofColor } from '@whiskerton/content';
import type { HouseState, MapData, ObjectId, ResidentId, ResidentPublic, TownId, TownInfo, UserId, Vec2, VillagerId, VillagerPublic, WorldObject } from '@whiskerton/shared';
import { generateTown } from '@whiskerton/shared/map/generate';   // subpath-only (not in the barrel)
import { comboKey, generateVillager } from '@whiskerton/shared/villagers/generate';

export interface Showcase {
  town: TownInfo; map: MapData; objects: WorldObject[];
  /** The friends whose houses stand in the town (their colour dyes the fence and flowers). */
  residents: Record<ResidentId, ResidentPublic>;
  villagers: VillagerPublic[];
  /** The plaza's centre (tile coordinates): where the camera turns and swoops, and where the villagers stroll. */
  plaza: Vec2;
}
const id = <T extends string>(prefix: string, n: number) => `${prefix}_${String(n).padStart(16, '0')}` as T;

export function showcaseTown(seed: number = tuning.title.seed): Showcase {
  const gen = generateTown(seed), map = gen.map, T = tuning.title;
  const p = map.sites.plaza, plaza = { x: p.x + p.w / 2, y: p.y + p.h / 2 };
  const objects: WorldObject[] = gen.objects.map((o, i) => ({ ...o, id: id<ObjectId>('o', i), owner: null }));
  // Plots by distance from the plaza; friends and villagers alternate on the nearest, so both show round the square.
  const near = map.plots.map((pl, i) => ({ i, d: Math.hypot(pl.house.x + 1.5 - plaza.x, pl.house.y + 1.5 - plaza.y) })).sort((a, b) => a.d - b.d);
  const friends = [...T.friendHouses], owners: ({ kind: 'friend'; f: (typeof T.friendHouses)[number] } | { kind: 'villager' })[] = [];
  let villagersLeft: number = T.villagerHouses;
  while (friends.length > 0 || villagersLeft > 0) {
    if (friends.length > 0) owners.push({ kind: 'friend', f: friends.shift()! });
    if (villagersLeft > 0) { owners.push({ kind: 'villager' }); villagersLeft--; }
  }
  const residents: Record<ResidentId, ResidentPublic> = {}, villagers: VillagerPublic[] = [];
  const taken = { names: new Set<string>(), combos: new Set<string>(), species: new Set<string>(), catchphrases: new Set<string>() };
  const house = (plot: number, owner: WorldObject['owner'], level: HouseLevel, roof: RoofColor, occupant: HouseState['occupant']) => {
    const pl = map.plots[plot]!;
    objects.push({ id: id<ObjectId>('o', objects.length), kind: 'house', catalogId: 'w.house', x: pl.house.x, y: pl.house.y, rot: 0, owner,
      state: { plot, level, roof, occupant, building: false } satisfies HouseState });
    objects.push({ id: id<ObjectId>('o', objects.length), kind: 'mailbox', catalogId: 'w.mailbox', x: pl.mailbox.x, y: pl.mailbox.y, rot: 0, owner,
      state: { plot } });
  };
  owners.forEach((o, k) => {
    const plot = near[k]!.i;
    if (o.kind === 'friend') {
      const r = id<ResidentId>('r', k), color = o.f.color as FriendColor, level = o.f.level as HouseLevel, roof = o.f.roof as RoofColor;
      residents[r] = { id: r, userId: id<UserId>('u', k), name: '', color, plot, role: 'resident', houseLevel: level, roof, door: 'open',
        pendingUpgrade: null, joinedAt: 0, look: { skin: 'skin2', eyes: 'round', hairStyle: 'short', hairColor: 'brown', shirt: 'friend', pants: 'denim', shoes: 'brown' } };
      house(plot, r, level, roof, { kind: 'resident', id: r });
      return;
    }
    const v: VillagerPublic = { ...generateVillager(seed, villagers.length, taken, plot, 0), id: id<VillagerId>('v', villagers.length) };
    villagers.push(v);
    taken.names.add(v.name); taken.combos.add(comboKey(v.species, v.personality)); taken.species.add(v.species); taken.catchphrases.add(v.catchphrase);
    const theme = villagerBank.houseThemes.find((t) => t.id === v.houseTheme) ?? villagerBank.houseThemes[0]!;
    house(plot, v.id, theme.level, theme.roof, { kind: 'villager', id: v.id });
  });
  const town: TownInfo = { id: id<TownId>('t', 0), name: '', hemisphere: 'north', timeZone: 'UTC', nativeFruit: gen.nativeFruit, seed, genVersion: map.genVersion,
    mayor: null, settings: { residentsCanInvite: false, instantMail: false, pro: false }, foundedAt: 0 };
  return { town, map, objects, residents, villagers, plaza };
}
